import React, { useState, useEffect } from 'react';
import client from '../api/client';
import { usePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';

const ConstructionStages = () => {
  const { canEdit } = usePermissions();
  const { showError, showSuccess } = useToast();
  const stagesCanEdit = canEdit(PAGE_PERMISSION_IDS.stages);
  const [stages, setStages] = useState([]);
  const [stageGroups, setStageGroups] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [insertAboveIndex, setInsertAboveIndex] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState({ name: '', order_index: 0, is_active: true });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stage_group_id: '',
    order_index: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStages();
    fetchStageGroups();
  }, []);

  useEffect(() => {
    if (!showForm && !showGroupForm) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showForm) resetForm();
        if (showGroupForm) {
          setShowGroupForm(false);
          setEditingGroup(null);
          setGroupFormData({ name: '', order_index: 0, is_active: true });
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showForm, showGroupForm]);

  const fetchStages = async () => {
    try {
      setLoading(true);
      const response = await client.get('/construction-stages');
      setStages(response.data);
    } catch (error) {
      console.error('Error fetching stages:', error);
      showError('Ошибка при загрузке этапов строительства');
    } finally {
      setLoading(false);
    }
  };

  const fetchStageGroups = async () => {
    try {
      const response = await client.get('/stage-groups');
      setStageGroups(response.data);
    } catch (error) {
      console.error('Error fetching stage groups:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        name: formData.name.trim(),
        description: formData.description ? formData.description.trim() : null,
        stage_group_id: formData.stage_group_id ? parseInt(formData.stage_group_id) : null,
        order_index: parseInt(formData.order_index) || 0,
        is_active: formData.is_active
      };

      if (editingStage) {
        const updateData = {};
        if (dataToSend.name !== editingStage.name) updateData.name = dataToSend.name;
        if (dataToSend.description !== editingStage.description) updateData.description = dataToSend.description;
        if (dataToSend.stage_group_id !== (editingStage.stage_group_id ?? null)) updateData.stage_group_id = dataToSend.stage_group_id;
        if (dataToSend.order_index !== editingStage.order_index) updateData.order_index = dataToSend.order_index;
        if (dataToSend.is_active !== editingStage.is_active) updateData.is_active = dataToSend.is_active;
        await client.put(`/construction-stages/${editingStage.id}`, updateData);
      } else {
        const created = await client.post('/construction-stages', dataToSend);
        if (insertAboveIndex !== null) {
          const newId = created.data.id;
          const before = stages.slice(0, insertAboveIndex).map(s => s.id);
          const after = stages.slice(insertAboveIndex).map(s => s.id);
          await client.post('/construction-stages/reorder', [...before, newId, ...after]);
        }
      }
      fetchStages();
      resetForm();
    } catch (error) {
      console.error('Error saving stage:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') showError(`Ошибка: ${detail}`);
      else if (Array.isArray(detail)) {
        const messages = detail.map(err => `${err.loc?.[err.loc.length - 1]}: ${err.msg}`).join(', ');
        showError(`Ошибки валидации: ${messages}`);
      } else if (detail?.msg) showError(`Ошибка: ${detail.msg}`);
      else showError('Ошибка при сохранении этапа строительства');
    }
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: groupFormData.name.trim(),
        order_index: parseInt(groupFormData.order_index) || 0,
        is_active: groupFormData.is_active
      };
      if (editingGroup) {
        await client.put(`/stage-groups/${editingGroup.id}`, data);
      } else {
        await client.post('/stage-groups', data);
      }
      fetchStageGroups();
      setShowGroupForm(false);
      setEditingGroup(null);
      setGroupFormData({ name: '', order_index: 0, is_active: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Ошибка при сохранении группы');
    }
  };

  const handleEdit = (stage) => {
    setEditingStage(stage);
    setFormData({
      name: stage.name,
      description: stage.description || '',
      stage_group_id: stage.stage_group_id ?? '',
      order_index: stage.order_index,
      is_active: stage.is_active
    });
    setInsertAboveIndex(null);
    setShowForm(true);
  };

  const handleInsertAbove = (index) => {
    setEditingStage(null);
    setInsertAboveIndex(index);
    setFormData({
      name: '',
      description: '',
      stage_group_id: stages[index]?.stage_group_id ?? '',
      order_index: index,
      is_active: true
    });
    setShowForm(true);
  };

  const handleDelete = async (stageId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот этап строительства?')) {
      try {
        await client.delete(`/construction-stages/${stageId}`);
        fetchStages();
      } catch (error) {
        showError('Ошибка при удалении этапа строительства');
      }
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('Удалить эту группу этапов?')) {
      try {
        await client.delete(`/stage-groups/${groupId}`);
        fetchStageGroups();
        fetchStages();
      } catch (error) {
        showError(error.response?.data?.detail || 'Ошибка при удалении группы');
      }
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newStages = [...stages];
    [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
    try {
      await client.post('/construction-stages/reorder', newStages.map(s => s.id));
      fetchStages();
    } catch (error) {
      fetchStages();
    }
  };

  const handleMoveDown = async (index) => {
    if (index === stages.length - 1) return;
    const newStages = [...stages];
    [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
    try {
      await client.post('/construction-stages/reorder', newStages.map(s => s.id));
      fetchStages();
    } catch (error) {
      fetchStages();
    }
  };

  const handleGroupMoveUp = async (index) => {
    if (index === 0) return;
    const newGroups = [...stageGroups];
    [newGroups[index - 1], newGroups[index]] = [newGroups[index], newGroups[index - 1]];
    try {
      await client.post('/stage-groups/reorder', newGroups.map(g => g.id));
      fetchStageGroups();
    } catch (error) {
      fetchStageGroups();
    }
  };

  const handleGroupMoveDown = async (index) => {
    if (index === stageGroups.length - 1) return;
    const newGroups = [...stageGroups];
    [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];
    try {
      await client.post('/stage-groups/reorder', newGroups.map(g => g.id));
      fetchStageGroups();
    } catch (error) {
      fetchStageGroups();
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingStage(null);
    setInsertAboveIndex(null);
    setFormData({
      name: '',
      description: '',
      stage_group_id: '',
      order_index: stages.length,
      is_active: true
    });
  };

  const handleSeed = async () => {
    if (!window.confirm('Очистить все группы и этапы, затем загрузить данные из таблицы? Графики будут отвязаны от этапов.')) return;
    try {
      const res = await client.post('/stage-groups/seed?clear_first=true');
      showSuccess(`${res.data.message}. Создано групп: ${res.data.created_groups}, этапов: ${res.data.created_stages}`);
      fetchStageGroups();
      fetchStages();
    } catch (error) {
      showError(error.response?.data?.detail || 'Ошибка при загрузке данных');
    }
  };

  if (loading) {
    return <div className="container-fluid"><h1>Загрузка...</h1></div>;
  }

  return (
    <div className="container-fluid">
      <h1>Управление этапами строительства</h1>
      {!stagesCanEdit && (
        <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
          Режим просмотра — изменение этапов и групп недоступно для вашей роли.
        </p>
      )}

      {/* Секция: Группа этапов */}
      <div className="card-full-width" style={{ marginBottom: '24px' }}>
        <h3>Группа этапов</h3>
        {stagesCanEdit && (
          <>
            <button className="btn btn-secondary btn-sm" onClick={handleSeed} style={{ marginBottom: '12px', marginRight: '8px' }}>
              Загрузить начальные данные
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowGroupForm(true); setEditingGroup(null); setGroupFormData({ name: '', order_index: stageGroups.length, is_active: true }); }} style={{ marginBottom: '12px' }}>
              Добавить группу
            </button>
          </>
        )}
        {showGroupForm && stagesCanEdit && (
          <div className="card" style={{ maxWidth: '500px', marginBottom: '16px' }}>
            <h4>{editingGroup ? 'Редактировать группу' : 'Новая группа'}</h4>
            <form onSubmit={handleGroupSubmit}>
              <div className="form-group">
                <label>Название группы *</label>
                <input type="text" className="form-control" value={groupFormData.name} onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label><input type="checkbox" checked={groupFormData.is_active} onChange={(e) => setGroupFormData({ ...groupFormData, is_active: e.target.checked })} /> Активна</label>
              </div>
              <button type="submit" className="btn btn-primary btn-sm">Сохранить</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowGroupForm(false); setEditingGroup(null); }} style={{ marginLeft: '8px' }}>Отмена</button>
            </form>
          </div>
        )}
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Порядок</th>
                <th>Название группы</th>
                <th style={{ width: '120px' }}>Сортировка</th>
                <th style={{ width: '180px' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {stageGroups.map((group, idx) => (
                <tr key={group.id}>
                  <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                  <td><strong>{group.name}</strong></td>
                  <td>
                    {stagesCanEdit ? (
                      <>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleGroupMoveUp(idx)} disabled={idx === 0} style={{ marginRight: '5px' }}>↑</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleGroupMoveDown(idx)} disabled={idx === stageGroups.length - 1}>↓</button>
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    {stagesCanEdit ? (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingGroup(group); setGroupFormData({ name: group.name, order_index: group.order_index, is_active: group.is_active }); setShowGroupForm(true); }} style={{ marginRight: '5px' }}>Редактировать</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteGroup(group.id)}>Удалить</button>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Только просмотр</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {stageGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>Нет групп. Нажмите «Загрузить начальные данные» или «Добавить группу».</div>
        )}
      </div>

      {/* Секция: Этапы строительства */}
      <div className="card-full-width">
        <h3>Этапы строительства</h3>
        {stagesCanEdit && (
          <button className="btn btn-primary" onClick={() => { setEditingStage(null); setInsertAboveIndex(null); setFormData({ name: '', description: '', stage_group_id: '', order_index: stages.length, is_active: true }); setShowForm(true); }} style={{ marginBottom: '20px' }}>
            Добавить этап
          </button>
        )}

        {showForm && stagesCanEdit && (
          <div className="card" style={{ maxWidth: '800px', marginBottom: '20px' }}>
            <h3>{editingStage ? 'Редактировать этап' : insertAboveIndex !== null ? `Вставить этап перед позицией ${insertAboveIndex + 1}` : 'Новый этап строительства'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Название этапа *</label>
                <input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Группа этапов</label>
                <select className="form-control" value={formData.stage_group_id} onChange={(e) => setFormData({ ...formData, stage_group_id: e.target.value })}>
                  <option value="">— Без группы —</option>
                  {stageGroups.map(g => (<option key={g.id} value={g.id}>{g.name}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea className="form-control" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label><input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} /> Активный этап</label>
              </div>
              <button type="submit" className="btn btn-primary">{editingStage ? 'Обновить' : 'Создать'}</button>
              <button type="button" className="btn btn-secondary" onClick={resetForm} style={{ marginLeft: '8px' }}>Отмена</button>
            </form>
          </div>
        )}

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Порядок</th>
                <th>Группа этапов</th>
                <th>Название</th>
                <th>Описание</th>
                <th style={{ width: '100px' }}>Статус</th>
                <th style={{ width: '160px' }}>Сортировка / Вставка</th>
                <th style={{ width: '200px' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((stage, index) => (
                <React.Fragment key={stage.id}>
                  <tr style={{ opacity: stage.is_active ? 1 : 0.6, backgroundColor: stage.is_active ? 'transparent' : '#f8f9fa' }}>
                    <td style={{ textAlign: 'center' }}>{index + 1}</td>
                    <td>{stage.stage_group?.name || '—'}</td>
                    <td><strong>{stage.name}</strong>{!stage.is_active && <span style={{ color: '#dc3545', marginLeft: '8px' }}>(Неактивен)</span>}</td>
                    <td>{stage.description || '—'}</td>
                    <td><span className={stage.is_active ? 'status-on-time' : 'status-delayed'}>{stage.is_active ? 'Активен' : 'Неактивен'}</span></td>
                    <td>
                      {stagesCanEdit ? (
                        <>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleMoveUp(index)} disabled={index === 0} style={{ marginRight: '4px' }} title="Вверх">↑</button>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleMoveDown(index)} disabled={index === stages.length - 1} style={{ marginRight: '4px' }} title="Вниз">↓</button>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => handleInsertAbove(index)} title="Вставить выше">+ выше</button>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      {stagesCanEdit ? (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(stage)} style={{ marginRight: '5px' }}>Редактировать</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(stage.id)}>Удалить</button>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Только просмотр</span>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {stages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            Нет этапов. Нажмите «Добавить этап» или «Загрузить начальные данные» в блоке выше.
          </div>
        )}
      </div>
    </div>
  );
};

export default ConstructionStages;
